import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Novel } from '../data/samples';
import { useBottomSheetDrag } from '../hooks/useBottomSheetDrag';
import type {
  BgmSoundType,
  EmphasisDotsStyleType,
  PageTransitionType,
  RubyDisplayType,
} from '../hooks/useReaderSettings';
import { SPRING_BOUNCY, SPRING_SMOOTH, SPRING_SNAPPY } from '../utils/animationPresets';
import { getThemeColors, isDarkTheme } from '../utils/theme';
import { AlignJustifyIcon, AlignRightIcon, CheckIcon, CompassIcon, TypeIcon, XIcon } from './Icons';
import { CommonSettingsTab } from './reader-settings/CommonSettingsTab';
import { DataManagementTab } from './reader-settings/DataManagementTab';
import { NovelSettingsTab } from './reader-settings/NovelSettingsTab';
import { Overlay } from './ui/Overlay';

export type NovelTheme = 'paper' | 'sepia' | 'night' | 'midnight' | 'sakura' | 'auto';
export type NovelLayout = 'vertical' | 'horizontal';
export type LineHeightPreset = 'tight' | 'normal' | 'relaxed' | 'loose';
export type MarginPreset = 'narrow' | 'normal' | 'wide';

interface ReaderSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  currentTheme: NovelTheme;
  setTheme: (theme: NovelTheme) => void;
  layout: NovelLayout;
  setLayout: (layout: NovelLayout) => void;
  fontSize: number;
  setFontSize: (size: number) => void;
  lineHeight: LineHeightPreset;
  setLineHeight: (lh: LineHeightPreset) => void;
  fontFamily: 'serif' | 'sans';
  setFontFamily: (font: 'serif' | 'sans') => void;
  margin: MarginPreset;
  setMargin: (m: MarginPreset) => void;
  bookModeEnabled: boolean;
  setBookModeEnabled: (enabled: boolean) => void;
  defaultTab?: 'novel' | 'common' | 'data';

  customNovels: Novel[];
  updateCustomNovels: (novels: Novel[]) => Promise<void>;
  showRemainingTime: boolean;
  setShowRemainingTime: (v: boolean) => void;

  // 全体設定項目
  pageTransition: PageTransitionType;
  setPageTransition: (t: PageTransitionType) => void;
  hapticsEnabled: boolean;
  setHapticsEnabled: (v: boolean) => void;
  soundVolume: number;
  setSoundVolume: (v: number) => void;
  bgmEnabled: boolean;
  setBgmEnabled: (v: boolean) => void;
  bgmVolume: number;
  setBgmVolume: (v: number) => void;
  bgmSound: BgmSoundType;
  setBgmSound: (s: BgmSoundType) => void;
  glowEnabled: boolean;
  setGlowEnabled: (v: boolean) => void;
  glowIntensity: number;
  setGlowIntensity: (v: number) => void;
  focusModeEnabled: boolean;
  setFocusModeEnabled: (v: boolean) => void;
  focusLineCount: number;
  setFocusLineCount: (v: number) => void;
  tateChuYokoEnabled: boolean;
  setTateChuYokoEnabled: (v: boolean) => void;
  radialMenuEnabled: boolean;
  setRadialMenuEnabled: (v: boolean) => void;
  bookmarkGestureEnabled: boolean;
  setBookmarkGestureEnabled: (v: boolean) => void;
  shareCardEnabled: boolean;
  setShareCardEnabled: (v: boolean) => void;
  rubyDisplay: RubyDisplayType;
  setRubyDisplay: (display: RubyDisplayType) => void;
  emphasisDotsStyle: EmphasisDotsStyleType;
  setEmphasisDotsStyle: (style: EmphasisDotsStyleType) => void;
}

export const ReaderSettings: React.FC<ReaderSettingsProps> = ({
  isOpen,
  onClose,
  currentTheme,
  setTheme,
  layout,
  setLayout,
  fontSize,
  setFontSize,
  lineHeight,
  setLineHeight,
  fontFamily,
  setFontFamily,
  margin,
  setMargin,
  bookModeEnabled,
  setBookModeEnabled,
  defaultTab,
  pageTransition,
  setPageTransition,
  hapticsEnabled,
  setHapticsEnabled,
  soundVolume,
  setSoundVolume,
  bgmEnabled,
  setBgmEnabled,
  bgmVolume,
  setBgmVolume,
  bgmSound,
  setBgmSound,
  glowEnabled,
  setGlowEnabled,
  glowIntensity,
  setGlowIntensity,
  focusModeEnabled,
  setFocusModeEnabled,
  focusLineCount,
  setFocusLineCount,
  tateChuYokoEnabled,
  setTateChuYokoEnabled,
  radialMenuEnabled,
  setRadialMenuEnabled,
  bookmarkGestureEnabled,
  setBookmarkGestureEnabled,
  shareCardEnabled,
  setShareCardEnabled,
  rubyDisplay,
  setRubyDisplay,
  emphasisDotsStyle,
  setEmphasisDotsStyle,
  customNovels,
  updateCustomNovels,
  showRemainingTime,
  setShowRemainingTime,
}) => {
  const [activeTab, setActiveTab] = useState<'novel' | 'common' | 'data'>(defaultTab || 'novel');
  const [scrollTop, setScrollTop] = useState(0);
  const [showEffectsAccordion, setShowEffectsAccordion] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const settingsScrollRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const handleClose = useCallback(() => {
    const previousFocus = previousFocusRef.current;
    if (previousFocus?.isConnected) {
      previousFocus.focus({ preventScroll: true });
    } else if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onClose();
  }, [onClose]);
  const dragHandlers = useBottomSheetDrag({
    sheetRef,
    scrollRegionRef: settingsScrollRef,
    onClose: handleClose,
  });

  const selectTab = (tab: 'novel' | 'common' | 'data') => {
    setActiveTab(tab);
    if (settingsScrollRef.current) {
      settingsScrollRef.current.scrollTop = 0;
    }
    setScrollTop(0);
  };

  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }
      if (e.key === 'Tab' && sheetRef.current) {
        const focusable = Array.from(
          sheetRef.current.querySelectorAll<HTMLElement>(
            'button, input, select, textarea, [href], [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((element) => !element.hasAttribute('disabled') && element.offsetParent !== null);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (
        document.activeElement instanceof HTMLElement &&
        sheetRef.current?.contains(document.activeElement)
      ) {
        previousFocusRef.current?.focus();
      }
    };
  }, [handleClose, isOpen]);

  const isDark = isDarkTheme(currentTheme);
  const { bgCol, borderCol, cardBg } = getThemeColors(isDark);

  // アクティブボタンはモノクローム高コントラストに
  const activeBtnBg = isDark
    ? 'bg-[#FAF8F5] text-[#111111] font-bold'
    : 'bg-[#111111] text-[#FFFFFF] font-bold';
  const inactiveBtnBg = isDark
    ? 'bg-[#1C1C1E] text-neutral-400 hover:text-neutral-200'
    : 'bg-[#F3F3F2] text-neutral-600 hover:text-neutral-800';

  const themes: { id: NovelTheme; name: string; bg: string; text: string }[] = [
    { id: 'paper', name: '生成り', bg: 'bg-[#FAF8F5]', text: 'text-[#2B2927]' },
    { id: 'sepia', name: 'セピア', bg: 'bg-[#F5ECDB]', text: 'text-[#453523]' },
    { id: 'sakura', name: '桜', bg: 'bg-[#FAF0F2]', text: 'text-[#47363A]' },
    { id: 'night', name: '夜', bg: 'bg-[#18181A]', text: 'text-[#E3E3E6]' },
    { id: 'midnight', name: '極夜', bg: 'bg-[#0B0F17]', text: 'text-[#D5DDE8]' },
    {
      id: 'auto',
      name: '自動',
      bg: 'bg-[#EAEAEA] dark:bg-[#1C1C1E]',
      text: 'text-neutral-850 dark:text-neutral-200',
    },
  ];

  const lineHeights: { id: LineHeightPreset; label: string }[] = [
    { id: 'tight', label: '狭め' },
    { id: 'normal', label: '標準' },
    { id: 'relaxed', label: '広め' },
    { id: 'loose', label: '超広め' },
  ];

  const marginPresets: { id: MarginPreset; label: string }[] = [
    { id: 'narrow', label: '狭め' },
    { id: 'normal', label: '標準' },
    { id: 'wide', label: '広め' },
  ];

  const applyHorizontalPreset = () => {
    setLayout('horizontal');
    setFontSize(18);
    setLineHeight('relaxed');
  };

  const applyVerticalPreset = () => {
    setLayout('vertical');
    setFontSize(18);
    setLineHeight('normal');
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
  };

  return (
    <AnimatePresence>
      {isOpen && <Overlay key="reader-settings-overlay" onClose={handleClose} />}

      {isOpen && (
        <motion.div
          key="reader-settings-sheet"
          ref={sheetRef}
          data-reader-settings-sheet="true"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={SPRING_SMOOTH}
          {...dragHandlers}
          className={`absolute bottom-0 left-0 w-full rounded-t-2xl z-50 shadow-2xl flex flex-col pt-4 pl-[max(20px,env(safe-area-inset-left))] pr-[max(20px,env(safe-area-inset-right))] pb-[max(env(safe-area-inset-bottom),28px)] space-y-4 border-t ${borderCol} ${bgCol} max-h-[85vh]`}
          style={{ touchAction: scrollTop === 0 ? 'pan-x' : 'auto' }}
        >
          {/* ハンドルバー */}
          <div
            data-sheet-drag-region="true"
            className="min-h-11 min-w-11 flex items-center justify-center self-center mb-1 cursor-row-resize active:cursor-grabbing touch-none"
          >
            <div className="w-11 h-1.5 bg-neutral-500/30 rounded-full" />
          </div>

          {/* ヘッダー */}
          <div
            data-sheet-drag-region="true"
            className="flex justify-between items-center border-b border-current/10 pb-2 touch-none"
          >
            <h3
              id="reader-settings-title"
              // biome-ignore lint/a11y/noRedundantRoles: needed for playwright selector
              // biome-ignore lint/a11y/useSemanticElements: needed for playwright selector
              role="heading"
              aria-level={3}
              className="font-serif font-bold text-base tracking-[0.12em] flex items-center space-x-2"
              {...({ name: '読書設定' } as React.HTMLAttributes<HTMLHeadingElement>)}
            >
              <CompassIcon size={15} strokeWidth={2} />
              <span>読書設定</span>
            </h3>
            <motion.button
              ref={closeButtonRef}
              type="button"
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.1 }}
              transition={SPRING_BOUNCY}
              onClick={handleClose}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label="閉じる"
              className={`w-11 h-11 rounded-md flex items-center justify-center transition-colors ${
                isDark
                  ? 'hover:bg-neutral-850 text-neutral-300'
                  : 'hover:bg-neutral-100 text-neutral-600'
              }`}
            >
              <XIcon size={16} strokeWidth={2} />
            </motion.button>
          </div>

          {/* タブ切り替えUI */}
          <div className="flex border-b border-current/10 pb-1 relative">
            <motion.button
              type="button"
              whileTap={{ scale: 0.96 }}
              onClick={() => selectTab('novel')}
              className={`flex-1 pb-2 text-xs font-bold text-center relative transition-colors ${
                activeTab === 'novel'
                  ? isDark
                    ? 'text-white'
                    : 'text-neutral-900'
                  : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
              }`}
            >
              小説設定
              {activeTab === 'novel' && (
                <motion.div
                  layoutId="activeTabUnderline"
                  transition={SPRING_SNAPPY}
                  className={`absolute bottom-0 left-0 right-0 h-0.5 ${isDark ? 'bg-white' : 'bg-neutral-900'}`}
                />
              )}
            </motion.button>
            <motion.button
              type="button"
              whileTap={{ scale: 0.96 }}
              onClick={() => selectTab('common')}
              className={`flex-1 pb-2 text-xs font-bold text-center relative transition-colors ${
                activeTab === 'common'
                  ? isDark
                    ? 'text-white'
                    : 'text-neutral-900'
                  : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
              }`}
            >
              全体設定
              {activeTab === 'common' && (
                <motion.div
                  layoutId="activeTabUnderline"
                  transition={SPRING_SNAPPY}
                  className={`absolute bottom-0 left-0 right-0 h-0.5 ${isDark ? 'bg-white' : 'bg-neutral-900'}`}
                />
              )}
            </motion.button>
            <motion.button
              type="button"
              whileTap={{ scale: 0.96 }}
              onClick={() => selectTab('data')}
              className={`flex-1 pb-2 text-xs font-bold text-center relative transition-colors ${
                activeTab === 'data'
                  ? isDark
                    ? 'text-white'
                    : 'text-neutral-900'
                  : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
              }`}
            >
              データ管理
              {activeTab === 'data' && (
                <motion.div
                  layoutId="activeTabUnderline"
                  transition={SPRING_SNAPPY}
                  className={`absolute bottom-0 left-0 right-0 h-0.5 ${isDark ? 'bg-white' : 'bg-neutral-900'}`}
                />
              )}
            </motion.button>
          </div>

          {/* 設定項目コンテンツエリア (スクロール対応・F3競合回避) */}
          <div
            ref={settingsScrollRef}
            data-sheet-drag-region="true"
            data-settings-scroll-region="true"
            onScroll={handleScroll}
            className="overflow-y-auto max-h-[50vh] pr-1 space-y-5 scrollbar-thin touch-auto"
          >
            {activeTab === 'novel' ? (
              <NovelSettingsTab
                layout={layout}
                setLayout={setLayout}
                fontSize={fontSize}
                setFontSize={setFontSize}
                lineHeight={lineHeight}
                setLineHeight={setLineHeight}
                fontFamily={fontFamily}
                setFontFamily={setFontFamily}
                margin={margin}
                setMargin={setMargin}
                bookModeEnabled={bookModeEnabled}
                setBookModeEnabled={setBookModeEnabled}
                currentTheme={currentTheme}
                setTheme={setTheme}
                focusModeEnabled={focusModeEnabled}
                setFocusModeEnabled={setFocusModeEnabled}
                focusLineCount={focusLineCount}
                setFocusLineCount={setFocusLineCount}
                rubyDisplay={rubyDisplay}
                setRubyDisplay={setRubyDisplay}
                tateChuYokoEnabled={tateChuYokoEnabled}
                setTateChuYokoEnabled={setTateChuYokoEnabled}
                isDark={isDark}
                emphasisDotsStyle={emphasisDotsStyle}
                setEmphasisDotsStyle={setEmphasisDotsStyle}
              />
            ) : activeTab === 'common' ? (
              <CommonSettingsTab
                pageTransition={pageTransition}
                setPageTransition={setPageTransition}
                radialMenuEnabled={radialMenuEnabled}
                setRadialMenuEnabled={setRadialMenuEnabled}
                bookmarkGestureEnabled={bookmarkGestureEnabled}
                setBookmarkGestureEnabled={setBookmarkGestureEnabled}
                shareCardEnabled={shareCardEnabled}
                setShareCardEnabled={setShareCardEnabled}
                bgmEnabled={bgmEnabled}
                setBgmEnabled={setBgmEnabled}
                bgmVolume={bgmVolume}
                setBgmVolume={setBgmVolume}
                bgmSound={bgmSound}
                setBgmSound={setBgmSound}
                soundVolume={soundVolume}
                setSoundVolume={setSoundVolume}
                hapticsEnabled={hapticsEnabled}
                setHapticsEnabled={setHapticsEnabled}
                glowEnabled={glowEnabled}
                setGlowEnabled={setGlowEnabled}
                glowIntensity={glowIntensity}
                setGlowIntensity={setGlowIntensity}
                tateChuYokoEnabled={tateChuYokoEnabled}
                setTateChuYokoEnabled={setTateChuYokoEnabled}
                focusModeEnabled={focusModeEnabled}
                setFocusModeEnabled={setFocusModeEnabled}
                focusLineCount={focusLineCount}
                setFocusLineCount={setFocusLineCount}
                isDark={isDark}
                showRemainingTime={showRemainingTime}
                setShowRemainingTime={setShowRemainingTime}
              />
            ) : (
              <DataManagementTab
                isDark={isDark}
                customNovels={customNovels}
                updateCustomNovels={updateCustomNovels}
              />
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
